import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { downloadReportCsv, printReportSummary } from "@/lib/reportExport";
import { businessHeader } from "@/lib/outlet";
import { useAuth } from "@/contexts/AuthContext";
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
    IndianRupee, Receipt, TrendingUp, TrendingDown, BarChart3, ChevronRight,
    Download, Printer, Users, Calendar, Crown, Percent, FileText, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

const GOLD = "#C9A227";
const GOLD_SOFT = "#D4AF37";
const CHOCOLATE = "#3D2817";
const MAROON = "#4A0E17";

const PAYMENT_COLORS = {
    cash: "#C9A227",
    upi: "#3B82C4",
    card: "#D9468F",
    razorpay: "#4A0E17",
    wallet: "#2D9B6E",
};

const SERVICE_COLORS = ["#4A0E17", "#B85C7A", "#D9468F", "#3B82C4", "#C9A227", "#2D9B6E", "#F4A89A", "#8B5CF6"];

const STAFF_BAR_COLORS = [
    { color: MAROON, gradient: ["#7A2438", "#4A0E17"] },
    { color: GOLD, gradient: ["#E8C04A", "#C9A227"] },
    { color: "#D9468F", gradient: ["#F06BA8", "#D9468F"] },
    { color: "#3B82C4", gradient: ["#6BA8E8", "#3B82C4"] },
    { color: "#B85C7A", gradient: ["#D98FA5", "#B85C7A"] },
    { color: "#2D9B6E", gradient: ["#5BC49A", "#2D9B6E"] },
];

const STATUS_COLORS = {
    booked: "#4A0E17",
    confirmed: "#7A2438",
    arrived: "#D9468F",
    "in-service": "#F06BA8",
    completed: "#C9A227",
    cancelled: "#B8859C",
    "no-show": "#9A9088",
};

const MODE_LABELS = {
    cash: "Cash",
    upi: "UPI",
    card: "Card",
    razorpay: "Razorpay",
    wallet: "Wallet",
};

const KPI_ICON = {
    revenue: { bg: "#FDF3DC", color: GOLD, sub: "#9A7B10", wash: "linear-gradient(150deg, #FFFCF6 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(201,162,39,0.14) 0%, transparent 52%)", accent: GOLD },
    invoices: { bg: "#E0EDFA", color: "#3B82C4", sub: "#2E6BA8", wash: "linear-gradient(150deg, #F5F9FE 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(59,130,196,0.12) 0%, transparent 52%)", accent: "#3B82C4" },
    ticket: { bg: "#FBE4EF", color: "#D9468F", sub: "#B83A72", wash: "linear-gradient(150deg, #FDF6F9 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(217,70,143,0.11) 0%, transparent 52%)", accent: "#D9468F" },
    gst: { bg: "#EDE8F5", color: "#7C3AED", sub: "#5B21B6", wash: "linear-gradient(150deg, #F9F7FD 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(124,58,237,0.1) 0%, transparent 52%)", accent: "#7C3AED" },
    customers: { bg: "#DCEEDC", color: "#2D9B6E", sub: "#1F7A55", wash: "linear-gradient(150deg, #F4FBF7 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(45,155,110,0.11) 0%, transparent 52%)", accent: "#2D9B6E" },
    discount: { bg: "#FCE8E4", color: "#C84B31", sub: "#9A4A32", wash: "linear-gradient(150deg, #FFF8F6 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(200,75,49,0.12) 0%, transparent 52%)", accent: "#C84B31" },
    membership: { bg: "#FDF3DC", color: GOLD, sub: "#9A7B10", wash: "linear-gradient(150deg, #FFFCF6 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(201,162,39,0.14) 0%, transparent 52%)", accent: GOLD },
    outstanding: { bg: "#FDE8EC", color: MAROON, sub: "#7A2438", wash: "linear-gradient(150deg, #FFF5F7 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(74,14,23,0.1) 0%, transparent 52%)", accent: MAROON },
    labor: { bg: "#FBE4EF", color: "#D9468F", sub: "#B83A72", wash: "linear-gradient(150deg, #FDF6F9 0%, #FFFFFF 55%)", glow: "radial-gradient(circle at 100% 0%, rgba(217,70,143,0.11) 0%, transparent 52%)", accent: "#D9468F" },
};

function fmtDay(dateStr) {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtStatus(s) {
    return (s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function GoldDivider({ className = "max-w-[200px]" }) {
    return (
        <div className={`flex items-center ${className}`}>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: GOLD }}>◆</span>
            <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD_SOFT}B3, transparent)` }} />
        </div>
    );
}

function ChartCard({ title, subtitle, children, className = "", testId }) {
    return (
        <div
            className={`relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.04)] sm:p-6 ${className}`}
            data-testid={testId}
        >
            <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
            <div className="mb-4 sm:mb-5">
                <h2 className="text-lg font-bold sm:text-xl" style={{ color: CHOCOLATE }}>{title}</h2>
                {subtitle && <p className="mt-1 text-xs font-medium text-[#8A8178]">{subtitle}</p>}
                <div className="mt-2 flex max-w-[160px] items-center">
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                    <span className="px-2 text-[10px] leading-none text-[#D4AF37]">◆</span>
                    <div className="h-px flex-1 bg-[#D4AF37]/65" />
                </div>
            </div>
            {children}
        </div>
    );
}

const PERIOD_OPTIONS = [
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
    { value: 90, label: "90 days" },
];

const fieldClass =
    "border-[#E8E4DE] bg-[#FFFEF9] focus-visible:border-[#D4AF37]/55 focus-visible:ring-[#D4AF37]/20";

const navBtn =
    "border-[#E8E4DE] bg-white text-[#3D2817] shadow-sm hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]";

const fmtDateInput = (d) => new Date(d).toISOString().slice(0, 10);

function defaultCustomRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { from: fmtDateInput(start), to: fmtDateInput(end) };
}

function PeriodSelect({ mode, customRange, onPreset, onOpenCustom }) {
    const selectValue = mode === "custom" ? "custom" : String(mode);
    const customLabel = customRange
        ? `${customRange.from} → ${customRange.to}`
        : "Custom range…";

    return (
        <select
            value={selectValue}
            onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") onOpenCustom();
                else onPreset(Number(v));
            }}
            className="h-9 max-w-[200px] cursor-pointer truncate rounded-lg border border-[#E8E4DE] bg-white px-2.5 pr-7 text-xs font-semibold text-[#3D2817] shadow-sm outline-none transition-colors hover:border-[#D4AF37]/45 focus:border-[#D4AF37]/55 focus:ring-2 focus:ring-[#D4AF37]/20"
            data-testid="reports-period-select"
            aria-label="Report period"
        >
            {PERIOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} data-testid={`days-${o.value}`}>
                    {o.label}
                </option>
            ))}
            <option value="custom" data-testid="days-custom">{customLabel}</option>
        </select>
    );
}

function DateRangeDialog({ open, from, to, onFromChange, onToChange, onClose, onApply }) {
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-sm rounded-xl border-[#E8E4DE] p-5" data-testid="reports-range-dialog">
                <DialogHeader>
                    <DialogTitle className="text-lg font-bold" style={{ color: MAROON }}>Custom date range</DialogTitle>
                    <p className="text-xs font-medium text-[#8A8178]">Select start and end dates for the report.</p>
                </DialogHeader>
                <div className="space-y-4 pt-1">
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>Start date</Label>
                        <Input
                            type="date"
                            value={from}
                            onChange={(e) => onFromChange(e.target.value)}
                            max={to || undefined}
                            className={fieldClass}
                            data-testid="reports-range-from"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-xs font-bold uppercase tracking-wide" style={{ color: CHOCOLATE }}>End date</Label>
                        <Input
                            type="date"
                            value={to}
                            onChange={(e) => onToChange(e.target.value)}
                            min={from || undefined}
                            max={fmtDateInput(new Date())}
                            className={fieldClass}
                            data-testid="reports-range-to"
                        />
                    </div>
                </div>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" className={navBtn} onClick={onClose}>Cancel</Button>
                    <Button
                        type="button"
                        className="bg-[#4A0E17] font-semibold hover:bg-[#3a0b12]"
                        onClick={onApply}
                        data-testid="reports-range-apply"
                    >
                        Apply range
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReportStatCard({ icon: Icon, label, value, sub, iconTone = "revenue", to }) {
    const theme = KPI_ICON[iconTone] ?? KPI_ICON.revenue;

    const card = (
        <div
            className="group relative flex h-full min-h-[148px] flex-col overflow-hidden rounded-xl border border-[#E8E4DE]/80 p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D4AF37]/30 hover:shadow-[0_12px_32px_rgba(45,41,38,0.08)] sm:min-h-[156px] sm:p-6"
            style={{ background: theme.wash }}
        >
            <div
                className="pointer-events-none absolute -right-3 -top-3 size-24"
                style={{ background: theme.glow }}
                aria-hidden
            />
            <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{ background: `linear-gradient(90deg, ${MAROON} 0%, ${theme.accent} 50%, ${MAROON}CC 100%)` }}
                aria-hidden
            />

            <div className="relative flex shrink-0 items-start justify-between gap-2">
                <p className="min-h-[2.75rem] max-w-[calc(100%-2.75rem)] pt-0.5 text-[11px] font-extrabold uppercase leading-snug tracking-[0.14em]" style={{ color: CHOCOLATE }}>
                    {label}
                </p>
                <div
                    className="flex size-10 shrink-0 items-center justify-center rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-1 ring-white/90 transition-transform duration-300 group-hover:scale-105"
                    style={{ backgroundColor: theme.bg }}
                >
                    <Icon className="size-[18px]" style={{ color: theme.color }} strokeWidth={2.25} />
                </div>
            </div>

            <p className="relative mt-3 shrink-0 truncate text-[1.875rem] font-extrabold leading-none tracking-tight tabular-nums sm:text-[2rem]" style={{ color: CHOCOLATE }}>
                {value}
            </p>

            <p
                className="relative mt-auto min-h-[1.25rem] truncate pt-2.5 text-[12px] font-extrabold tracking-wide sm:text-[13px]"
                style={{ color: sub ? theme.sub : "transparent" }}
            >
                {sub || "\u00A0"}
            </p>

            {to && (
                <ChevronRight className="absolute bottom-5 right-5 size-4 text-[#8A8178] opacity-0 transition-opacity group-hover:opacity-100 sm:bottom-6 sm:right-6" />
            )}
        </div>
    );

    if (!to) return card;

    return (
        <Link to={to} className="block h-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]/60">
            {card}
        </Link>
    );
}

function GrowthBadge({ pct }) {
    if (pct == null) {
        return <span className="text-[#8A8178]">No prior data</span>;
    }
    const up = pct >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-0.5 font-bold ${up ? "text-[#9A7B10]" : "text-[#4A0E17]"}`}>
            <Icon className="size-3" />
            {up ? "+" : ""}{pct}% vs prior
        </span>
    );
}

function ChartScroll({ long, scrollWidth, height, children }) {
    if (!long) return <div style={{ height }}>{children}</div>;
    return (
        <div className="scrollbar-chart-x overflow-x-auto pb-2">
            <div style={{ width: scrollWidth, height }}>{children}</div>
        </div>
    );
}

function RevenueDot({ cx, cy }) {
    if (cx == null || cy == null) return null;
    return (
        <g>
            <circle cx={cx} cy={cy} r={5} fill="#FFF8E7" stroke={MAROON} strokeWidth={2} />
            <circle cx={cx} cy={cy} r={2} fill={GOLD_SOFT} />
        </g>
    );
}

function RevenueTrendChart({ trend, days }) {
    const formatted = useMemo(() => trend.map((d) => ({ ...d, label: fmtDay(d.date) })), [trend]);
    const chartHeight = 280;
    const longData = formatted.length > 14;
    const scrollWidth = formatted.length * 44;
    const hasRevenue = formatted.some((d) => d.revenue > 0);

    const chart = (
        <ResponsiveContainer width="100%" height={chartHeight}>
            <AreaChart data={formatted} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                <defs>
                    <linearGradient id="reportsRevenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={MAROON} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={MAROON} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="#E8E4DE" vertical={false} />
                <XAxis dataKey="label" stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} dy={8} interval={longData ? "preserveStartEnd" : 0} />
                <YAxis domain={[0, "auto"]} stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `₹${v / 1000}K` : `₹${v}`)} width={48} />
                <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E8E4DE", borderRadius: 8, fontSize: 12, color: MAROON }}
                    formatter={(v) => [inr(v), "Revenue"]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ""}
                    labelStyle={{ color: "#8A8178", fontWeight: 700 }}
                />
                <Area type="monotone" dataKey="revenue" stroke={MAROON} strokeWidth={2.5} fill="url(#reportsRevenueFill)" dot={formatted.length <= 31 ? <RevenueDot /> : false} activeDot={{ r: 6, fill: GOLD_SOFT, stroke: MAROON, strokeWidth: 2 }} />
            </AreaChart>
        </ResponsiveContainer>
    );

    return (
        <ChartCard title={`Daily revenue`} subtitle={`Last ${days} days`} testId="reports-revenue-chart">
            {!hasRevenue ? (
                <p className="py-12 text-center text-sm font-semibold text-[#8A8178]">No revenue in this period.</p>
            ) : (
                <ChartScroll long={longData} scrollWidth={scrollWidth} height={chartHeight}>{chart}</ChartScroll>
            )}
        </ChartCard>
    );
}

function InvoiceVolumeChart({ trend, days }) {
    const formatted = useMemo(() => trend.map((d) => ({ ...d, label: fmtDay(d.date) })), [trend]);
    const chartHeight = 220;
    const longData = formatted.length > 14;
    const scrollWidth = formatted.length * 44;
    const hasData = formatted.some((d) => d.count > 0);

    const chart = (
        <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={formatted} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#E8E4DE" vertical={false} />
                <XAxis dataKey="label" stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} dy={8} interval={longData ? "preserveStartEnd" : 0} />
                <YAxis allowDecimals={false} stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} width={32} />
                <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #E8E4DE", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => [v, "Invoices"]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.date ?? ""}
                />
                <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
        </ResponsiveContainer>
    );

    return (
        <ChartCard title="Invoice volume" subtitle={`Paid invoices per day · ${days} days`} testId="reports-invoice-chart">
            {!hasData ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No invoices in this period.</p>
            ) : (
                <ChartScroll long={longData} scrollWidth={scrollWidth} height={chartHeight}>{chart}</ChartScroll>
            )}
        </ChartCard>
    );
}

function PaymentModeChart({ payments }) {
    const total = payments.reduce((sum, p) => sum + p.amount, 0);
    const chartData = payments.map((p) => ({
        ...p,
        label: MODE_LABELS[p.mode] ?? p.mode,
        color: PAYMENT_COLORS[p.mode] ?? GOLD_SOFT,
        pct: total ? Math.round((p.amount / total) * 100) : 0,
    }));

    return (
        <ChartCard title="Payment split" subtitle="Revenue by mode" className="flex h-full flex-col" testId="reports-payment-chart">
            {chartData.length === 0 ? (
                <p className="flex flex-1 items-center py-8 text-sm font-semibold text-[#8A8178]">No payment data yet.</p>
            ) : (
                <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
                    <div className="relative size-[140px] shrink-0 sm:size-[156px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={chartData} dataKey="amount" nameKey="label" cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} stroke="#fff" strokeWidth={3}>
                                    {chartData.map((entry) => <Cell key={entry.mode} fill={entry.color} />)}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="pointer-events-none absolute left-1/2 top-1/2 w-[72px] -translate-x-1/2 -translate-y-1/2 text-center">
                            <p className="text-[10px] font-semibold leading-none text-[#8A8178]">Total</p>
                            <p className="mt-1 text-base font-bold leading-tight tabular-nums" style={{ color: CHOCOLATE }}>{inr(total)}</p>
                        </div>
                    </div>
                    <div className="w-full min-w-0 flex-1">
                        {chartData.map((p, i) => (
                            <div key={p.mode} className={`flex items-center justify-between gap-3 py-2 ${i < chartData.length - 1 ? "border-b border-[#E8E4DE]" : ""}`}>
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: p.color }} />
                                    <span className="truncate text-[13px] font-semibold" style={{ color: CHOCOLATE }}>{p.label}</span>
                                </div>
                                <div className="shrink-0 text-right">
                                    <p className="text-[13px] font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(p.amount)}</p>
                                    <p className="text-[11px] font-semibold text-[#8A8178]">{p.pct}%</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </ChartCard>
    );
}

function getStaffYMax(maxAmount) {
    if (maxAmount <= 0) return 1000;
    const step = maxAmount <= 5000 ? 1000 : 5000;
    return Math.max(step * 4, Math.ceil(maxAmount / step) * step);
}

function StaffRevenueChart({ staff }) {
    const peak = Math.max(...staff.map((s) => s.amount), 0);
    const yMax = getStaffYMax(peak);
    const yTicks = [0, 1, 2, 3, 4].map((i) => Math.round((yMax / 4) * (4 - i)));
    const PLOT_H = 160;
    const FOOTER_H = 48;

    return (
        <ChartCard title="Staff performance" subtitle="Revenue attributed per stylist" testId="reports-staff-chart">
            {staff.length === 0 ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No staff revenue tracked yet.</p>
            ) : (
                <div className="flex gap-3">
                    <div className="flex w-8 shrink-0 flex-col justify-between pt-0.5 text-[10px] font-semibold tabular-nums text-[#8A8178]" style={{ height: PLOT_H }}>
                        {yTicks.map((tick) => <span key={tick}>{tick >= 1000 ? `₹${tick / 1000}k` : `₹${tick}`}</span>)}
                    </div>
                    <div className="relative min-w-0 flex-1">
                        <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between" style={{ height: PLOT_H }}>
                            {yTicks.map((tick) => <div key={tick} className="border-t border-dashed border-[#E8E4DE]" aria-hidden />)}
                        </div>
                        <div className="scrollbar-chart-x relative flex justify-around gap-2 overflow-x-auto px-1" style={{ height: PLOT_H + FOOTER_H }}>
                            {staff.map((item, idx) => {
                                const theme = STAFF_BAR_COLORS[idx % STAFF_BAR_COLORS.length];
                                const barH = yMax > 0 ? Math.max(item.amount > 0 ? 10 : 0, (item.amount / yMax) * PLOT_H) : 0;
                                return (
                                    <div key={item.name} className="flex min-w-[52px] max-w-[72px] flex-1 flex-col items-center">
                                        <div className="relative flex w-full flex-col items-center justify-end" style={{ height: PLOT_H }}>
                                            <span className="mb-1 text-[10px] font-extrabold tabular-nums" style={{ color: CHOCOLATE }}>
                                                {item.amount >= 1000 ? `₹${(item.amount / 1000).toFixed(0)}k` : inr(item.amount)}
                                            </span>
                                            <div className="w-full min-w-[28px] rounded-t-lg" style={{ height: barH, background: `linear-gradient(180deg, ${theme.gradient[0]} 0%, ${theme.gradient[1]} 100%)`, boxShadow: item.amount > 0 ? `0 4px 14px ${theme.color}33` : "none" }} />
                                        </div>
                                        <span className="mt-2 w-full truncate text-center text-[10px] font-bold" style={{ color: CHOCOLATE }} title={item.name}>
                                            {item.name.split(" ")[0]}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </ChartCard>
    );
}

const PAY_TYPE_LABELS = {
    commission: "Commission",
    fixed: "Fixed",
    hybrid: "Hybrid",
};

function StaffPayrollTable({ rows, laborTotal, laborPct, revenueTotal }) {
    return (
        <ChartCard
            title="Staff payroll"
            subtitle={laborPct != null ? `Labor cost ${laborPct}% of revenue · target 35–45%` : "Earnings for this period"}
            testId="reports-staff-payroll"
        >
            {rows.length === 0 ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No payroll data for this period.</p>
            ) : (
                <div className="scrollbar-salon -mx-1 overflow-x-auto px-1">
                    <table className="w-full min-w-[760px] text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8A8178]">
                                <th className="px-3 py-2.5">Staff</th>
                                <th className="px-3 py-2.5">Pay type</th>
                                <th className="px-3 py-2.5 text-right">Revenue</th>
                                <th className="px-3 py-2.5 text-right">Base</th>
                                <th className="px-3 py-2.5 text-right">Commission</th>
                                <th className="px-3 py-2.5 text-right">Total pay</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.staff_id} className="border-b border-[#E8E4DE]/70 transition-colors hover:bg-[#FAF9F6]/60">
                                    <td className="px-3 py-2.5">
                                        <div className="font-bold" style={{ color: CHOCOLATE }}>{row.name}</div>
                                        <div className="text-[11px] capitalize text-[#8A8178]">{row.role}</div>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs font-semibold text-[#5C534C]">
                                        {PAY_TYPE_LABELS[row.pay_type] || row.pay_type}
                                        {row.pay_type !== "fixed" && row.commission_pct > 0 && (
                                            <span className="text-[#8A8178]"> · {row.commission_pct}%</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-[#8A8178]">{inr(row.revenue)}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: CHOCOLATE }}>{inr(row.base_salary)}</td>
                                    <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: GOLD }}>{inr(row.commission)}</td>
                                    <td className="px-3 py-2.5 text-right font-bold tabular-nums" style={{ color: MAROON }}>{inr(row.total_earnings)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t border-[#E8E4DE] bg-[#FFFEF9]">
                                <td colSpan={5} className="px-3 py-2.5 text-right text-xs font-extrabold uppercase tracking-wide text-[#8A8178]">
                                    Total labor ({laborPct != null ? `${laborPct}% of ${inr(revenueTotal)}` : "period"})
                                </td>
                                <td className="px-3 py-2.5 text-right font-serif text-base font-bold tabular-nums" style={{ color: MAROON }}>{inr(laborTotal)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </ChartCard>
    );
}

function TopServicesChart({ services }) {
    const chartData = services.slice(0, 8).map((s, i) => ({
        ...s,
        shortName: s.name.length > 18 ? `${s.name.slice(0, 16)}…` : s.name,
        color: SERVICE_COLORS[i % SERVICE_COLORS.length],
    }));
    const chartHeight = Math.max(200, chartData.length * 36);

    return (
        <ChartCard title="Top services" subtitle="By revenue in period" testId="reports-services-chart">
            {chartData.length === 0 ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No service sales yet.</p>
            ) : (
                <div className="scrollbar-chart-y max-h-[320px] overflow-y-auto pr-1">
                    <ResponsiveContainer width="100%" height={chartHeight}>
                        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 24, left: 4, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="4 4" stroke="#E8E4DE" horizontal={false} />
                            <XAxis type="number" stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `₹${v / 1000}k` : `₹${v}`)} />
                            <YAxis type="category" dataKey="shortName" stroke="#8A8178" fontSize={10} tickLine={false} axisLine={false} width={100} />
                            <Tooltip
                                contentStyle={{ background: "#fff", border: "1px solid #E8E4DE", borderRadius: 8, fontSize: 12 }}
                                formatter={(v, _, props) => [inr(v), `${props.payload.count} sold`]}
                            />
                            <Bar dataKey="revenue" radius={[0, 4, 4, 0]} maxBarSize={20}>
                                {chartData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </ChartCard>
    );
}

function AppointmentBreakdown({ statuses, total }) {
    const max = Math.max(...statuses.map((s) => s.count), 1);

    return (
        <ChartCard title="Appointments" subtitle={`${total} bookings in period`} testId="reports-appt-chart">
            {statuses.length === 0 ? (
                <p className="py-8 text-center text-sm font-semibold text-[#8A8178]">No appointments in this period.</p>
            ) : (
                <div className="space-y-3">
                    {statuses.map((s) => {
                        const pct = Math.round((s.count / max) * 100);
                        const color = STATUS_COLORS[s.status] ?? GOLD;
                        return (
                            <div key={s.status}>
                                <div className="mb-1 flex justify-between text-xs font-semibold">
                                    <span style={{ color: CHOCOLATE }}>{fmtStatus(s.status)}</span>
                                    <span className="tabular-nums text-[#8A8178]">{s.count}</span>
                                </div>
                                <div className="h-2 overflow-hidden rounded-full bg-[#F0EDE8]">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </ChartCard>
    );
}

function GstSummary({ data }) {
    const money = data.money || {};
    return (
        <ChartCard title="Tax & discounts" subtitle="GST collected and concessions" testId="reports-gst-summary">
            <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] p-4">
                    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A8178]">Sub-total</p>
                    <p className="mt-2 text-xl font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(data.sub_total)}</p>
                </div>
                <div className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] p-4">
                    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A8178]">GST (CGST + SGST)</p>
                    <p className="mt-2 text-xl font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(data.gst_total)}</p>
                </div>
                <div className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] p-4">
                    <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A8178]">Discounts given</p>
                    <p className="mt-2 text-xl font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(data.discount_total)}</p>
                </div>
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-[#D4AF37]/30 bg-[#FDF3DC]/40 px-4 py-3">
                <span className="text-sm font-semibold" style={{ color: CHOCOLATE }}>Net collected</span>
                <span className="text-lg font-bold tabular-nums" style={{ color: MAROON }}>{inr(data.total)}</span>
            </div>
            {(money.membership_outstanding_total > 0 || money.membership_pending_from_new_sales > 0) && (
                <div className="mt-3 flex items-center justify-between rounded-lg border border-[#FDE8EC] bg-[#FFF5F7]/80 px-4 py-3">
                    <span className="text-sm font-semibold text-[#7A2438]">Still to collect (memberships)</span>
                    <span className="text-lg font-bold tabular-nums text-[#C84B31]">{inr(money.membership_outstanding_total)}</span>
                </div>
            )}
        </ChartCard>
    );
}

function MoneyBreakdown({ data }) {
    const money = data.money || {};
    const rows = [
        { label: "Services & POS", amount: money.service_collected, color: MAROON },
        { label: "Membership payments", amount: money.membership_collected, color: GOLD },
        { label: "Other (products, packages…)", amount: money.other_collected, color: "#3B82C4" },
    ].filter((r) => (r.amount || 0) > 0);
    const max = Math.max(...rows.map((r) => r.amount || 0), 1);

    return (
        <ChartCard title="Money collected" subtitle="Cash received in this period, by type" testId="reports-money-breakdown">
            {rows.length === 0 ? (
                <p className="py-8 text-center text-sm font-semibold text-[#8A8178]">No collections in this period.</p>
            ) : (
                <div className="space-y-4">
                    {rows.map((r) => (
                        <div key={r.label}>
                            <div className="mb-1 flex justify-between text-xs font-semibold">
                                <span style={{ color: CHOCOLATE }}>{r.label}</span>
                                <span className="tabular-nums" style={{ color: r.color }}>{inr(r.amount)}</span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-[#F0EDE8]">
                                <div
                                    className="h-full rounded-full transition-all"
                                    style={{ width: `${Math.round((r.amount / max) * 100)}%`, backgroundColor: r.color }}
                                />
                            </div>
                        </div>
                    ))}
                    <div className="grid gap-3 border-t border-[#E8E4DE] pt-4 sm:grid-cols-2">
                        <div className="rounded-lg border border-[#E8E4DE] bg-[#FFFEF9] p-3">
                            <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A8178]">New membership sales (value)</p>
                            <p className="mt-1 font-serif text-lg font-bold tabular-nums" style={{ color: CHOCOLATE }}>
                                {inr(money.membership_contract_value_in_period)}
                            </p>
                            <p className="mt-0.5 text-[11px] font-medium text-[#8A8178]">
                                {data.memberships_sold} activated · {inr(money.membership_collected_on_new_sales)} collected
                            </p>
                        </div>
                        <div className="rounded-lg border border-[#FDE8EC] bg-[#FFF5F7]/60 p-3">
                            <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#8A8178]">Pending from new sales</p>
                            <p className="mt-1 font-serif text-lg font-bold tabular-nums text-[#C84B31]">
                                {inr(money.membership_pending_from_new_sales)}
                            </p>
                            <p className="mt-0.5 text-[11px] font-medium text-[#8A8178]">Part payments not yet collected</p>
                        </div>
                    </div>
                </div>
            )}
        </ChartCard>
    );
}

function RevenueTypeChart({ types }) {
    const chartData = (types || []).map((t, i) => ({
        name: t.label,
        value: t.amount,
        color: SERVICE_COLORS[i % SERVICE_COLORS.length],
    }));
    const total = chartData.reduce((s, d) => s + d.value, 0);

    return (
        <ChartCard title="Revenue by category" subtitle="Split of money collected in period" testId="reports-revenue-type">
            {chartData.length === 0 ? (
                <p className="py-8 text-center text-sm font-semibold text-[#8A8178]">No revenue in this period.</p>
            ) : (
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                    <div className="h-[200px] w-full max-w-[200px] shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={48} outerRadius={78} paddingAngle={2}>
                                    {chartData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                                </Pie>
                                <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "#fff", border: "1px solid #E8E4DE", borderRadius: 8, fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <ul className="w-full flex-1 space-y-2 text-sm">
                        {chartData.map((d) => (
                            <li key={d.name} className="flex items-center justify-between gap-2">
                                <span className="flex items-center gap-2 font-semibold" style={{ color: CHOCOLATE }}>
                                    <span className="size-2.5 shrink-0 rounded-full" style={{ background: d.color }} />
                                    {d.name}
                                </span>
                                <span className="font-bold tabular-nums text-[#8A8178]">
                                    {inr(d.value)}
                                    <span className="ml-1 text-[10px] font-medium">({total ? Math.round((d.value / total) * 100) : 0}%)</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </ChartCard>
    );
}

function MembershipOutstandingTable({ rows, total }) {
    return (
        <ChartCard
            title="Membership dues outstanding"
            subtitle={total > 0 ? `${inr(total)} to collect · ${rows.length} member${rows.length !== 1 ? "s" : ""}` : "All membership balances cleared"}
            testId="reports-membership-outstanding"
        >
            {rows.length === 0 ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No pending membership balances.</p>
            ) : (
                <div className="scrollbar-salon -mx-1 overflow-x-auto px-1">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8A8178]">
                                <th className="px-3 py-2.5">Customer</th>
                                <th className="px-3 py-2.5">Plan</th>
                                <th className="px-3 py-2.5">Started</th>
                                <th className="px-3 py-2.5 text-right">Total</th>
                                <th className="px-3 py-2.5 text-right">Paid</th>
                                <th className="px-3 py-2.5 text-right">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.membership_id} className="border-b border-[#E8E4DE]/70 transition-colors hover:bg-[#FAF9F6]/60">
                                    <td className="px-3 py-2.5">
                                        <div className="font-bold" style={{ color: CHOCOLATE }}>{row.customer_name}</div>
                                        {row.customer_mobile && <div className="text-[11px] text-[#8A8178]">{row.customer_mobile}</div>}
                                    </td>
                                    <td className="px-3 py-2.5 font-semibold" style={{ color: GOLD }}>{row.plan_name}</td>
                                    <td className="px-3 py-2.5 text-[12px] font-medium text-[#8A8178]">
                                        {row.started_at ? new Date(row.started_at).toLocaleDateString("en-IN") : "—"}
                                    </td>
                                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-[#8A8178]">{inr(row.total_due)}</td>
                                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums" style={{ color: CHOCOLATE }}>{inr(row.total_paid)}</td>
                                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-[#C84B31]">{inr(row.balance_due)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t border-[#E8E4DE] bg-[#FFFEF9]">
                                <td colSpan={5} className="px-3 py-2.5 text-right text-xs font-extrabold uppercase tracking-wide text-[#8A8178]">Total outstanding</td>
                                <td className="px-3 py-2.5 text-right font-serif text-base font-bold tabular-nums text-[#C84B31]">{inr(total)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </ChartCard>
    );
}

function InvoiceLedger({ ledger }) {
    return (
        <ChartCard title="Invoice ledger" subtitle={`${ledger.length} paid invoice${ledger.length !== 1 ? "s" : ""} in period`} testId="reports-ledger">
            {ledger.length === 0 ? (
                <p className="py-10 text-center text-sm font-semibold text-[#8A8178]">No paid invoices in this period.</p>
            ) : (
                <div className="scrollbar-salon -mx-1 overflow-x-auto px-1">
                    <table className="w-full min-w-[720px] text-sm">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#8A8178]">
                                <th className="px-3 py-2.5">Invoice</th>
                                <th className="px-3 py-2.5">Type</th>
                                <th className="px-3 py-2.5">Customer</th>
                                <th className="px-3 py-2.5">Date</th>
                                <th className="px-3 py-2.5">Payment</th>
                                <th className="px-3 py-2.5 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ledger.map((row) => (
                                <tr key={row.id} className="border-b border-[#E8E4DE]/70 transition-colors hover:bg-[#FAF9F6]/60">
                                    <td className="px-3 py-2.5 font-bold" style={{ color: MAROON }}>{row.invoice_number}</td>
                                    <td className="px-3 py-2.5">
                                        <span className="rounded-full bg-[#FAF9F6] px-2 py-0.5 text-[10px] font-bold text-[#5C534C]">
                                            {row.sale_type_label || row.sale_type || "—"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="font-semibold" style={{ color: CHOCOLATE }}>{row.customer_name}</div>
                                        {row.customer_mobile && <div className="text-[11px] text-[#8A8178]">{row.customer_mobile}</div>}
                                    </td>
                                    <td className="px-3 py-2.5 text-[12px] font-medium text-[#8A8178]">
                                        {new Date(row.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ background: `${PAYMENT_COLORS[row.payment_mode] ?? GOLD}22`, color: PAYMENT_COLORS[row.payment_mode] ?? CHOCOLATE }}>
                                            {MODE_LABELS[row.payment_mode] ?? row.payment_mode}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right font-bold tabular-nums" style={{ color: CHOCOLATE }}>{inr(row.grand_total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </ChartCard>
    );
}

export default function Reports() {
    const { user } = useAuth();
    const [outlets, setOutlets] = useState([]);
    const [data, setData] = useState(null);
    const [periodMode, setPeriodMode] = useState(30);
    const [customRange, setCustomRange] = useState(null);
    const [rangeOpen, setRangeOpen] = useState(false);
    const [draftFrom, setDraftFrom] = useState("");
    const [draftTo, setDraftTo] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const openRangeDialog = () => {
        const def = customRange || defaultCustomRange();
        setDraftFrom(def.from);
        setDraftTo(def.to);
        setRangeOpen(true);
    };

    const applyCustomRange = () => {
        if (!draftFrom || !draftTo) {
            toast.error("Select both start and end dates");
            return;
        }
        if (draftTo < draftFrom) {
            toast.error("End date must be on or after start date");
            return;
        }
        setCustomRange({ from: draftFrom, to: draftTo });
        setPeriodMode("custom");
        setRangeOpen(false);
    };

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = periodMode === "custom" && customRange
                ? { date_from: customRange.from, date_to: customRange.to }
                : { days: periodMode };
            const r = await api.get("/reports/revenue", { params });
            setData(r.data);
        } catch {
            setError("Could not load reports. Check your connection and try again.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [periodMode, customRange]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/outlets")
            .then((r) => setOutlets(r.data || []))
            .catch(() => setOutlets([]));
    }, []);

    const business = useMemo(
        () => businessHeader(outlets, user),
        [outlets, user],
    );

    const reportDays = data?.period_days ?? (periodMode === "custom" ? 30 : periodMode);
    const avgTicket = data?.count ? data.total / data.count : 0;
    const activeDays = data?.by_day?.filter((d) => d.revenue > 0).length ?? 0;

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="reports-page">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                    <p className={EYEBROW} style={{ color: GOLD }}>Analytics</p>
                    <h1 className="mt-1.5 text-[1.375rem] font-bold leading-snug sm:text-[1.5rem]" style={{ color: CHOCOLATE }}>
                        Reports
                    </h1>
                    <GoldDivider className="mt-3 max-w-[200px]" />
                    <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold text-[#8A8178]">
                        <BarChart3 className="size-3.5 text-[#D4AF37]" />
                        {data ? `${data.period_start} → ${data.period_end}` : periodMode === "custom" ? "Custom range" : `Last ${periodMode} days`}
                        {data && (
                            <>
                                <span className="text-[#E8E4DE]">·</span>
                                <GrowthBadge pct={data.growth_pct} />
                            </>
                        )}
                    </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <PeriodSelect
                        mode={periodMode}
                        customRange={customRange}
                        onPreset={(d) => { setPeriodMode(d); setCustomRange(null); }}
                        onOpenCustom={openRangeDialog}
                    />
                    {data && (
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" size="sm" className={navBtn} onClick={() => downloadReportCsv(data, business)} data-testid="reports-export-csv">
                                <Download className="mr-1 size-3.5" /> CSV
                            </Button>
                            <Button type="button" variant="outline" size="sm" className={navBtn} onClick={() => printReportSummary(data, business)} data-testid="reports-print">
                                <Printer className="mr-1 size-3.5" /> Print
                            </Button>
                        </div>
                    )}
                </div>
            </header>

            <DateRangeDialog
                open={rangeOpen}
                from={draftFrom}
                to={draftTo}
                onFromChange={setDraftFrom}
                onToChange={setDraftTo}
                onClose={() => setRangeOpen(false)}
                onApply={applyCustomRange}
            />

            {loading && (
                <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-white px-6 py-14 text-center shadow-[0_4px_22px_rgba(45,41,38,0.04)]">
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-[#4A0E17]/85" />
                    <p className="text-sm font-semibold text-[#8A8178]">Loading reports…</p>
                </div>
            )}

            {error && !loading && (
                <div className="relative overflow-hidden rounded-xl border border-[#FDE8EC] bg-[#FFF5F7] px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-[#4A0E17]">{error}</p>
                    <Button type="button" size="sm" className="mt-4 bg-[#4A0E17] hover:bg-[#3a0b12]" onClick={load}>Retry</Button>
                </div>
            )}

            {data && !loading && (
                <>
                    <div className="grid grid-cols-1 items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        <ReportStatCard icon={IndianRupee} iconTone="revenue" label="Total revenue" value={inr(data.total)} sub={`${activeDays} active day${activeDays !== 1 ? "s" : ""}`} to="/app/pos" />
                        <ReportStatCard icon={Receipt} iconTone="invoices" label="Invoices" value={data.count} sub="Paid in period" to="/app/pos" />
                        <ReportStatCard icon={TrendingUp} iconTone="ticket" label="Avg ticket" value={inr(avgTicket)} sub="Per invoice" />
                        <ReportStatCard icon={Percent} iconTone="gst" label="GST collected" value={inr(data.gst_total)} sub="CGST + SGST" />
                        <ReportStatCard icon={Crown} iconTone="membership" label="Membership collected" value={inr(data.membership_collected_in_period)} sub={`${data.memberships_sold} sold · ${inr(data.membership_contract_value)} value`} to="/app/memberships" />
                        <ReportStatCard icon={Wallet} iconTone="outstanding" label="Outstanding dues" value={inr(data.membership_outstanding_total)} sub={data.membership_outstanding_count > 0 ? `${data.membership_outstanding_count} member${data.membership_outstanding_count !== 1 ? "s" : ""} pending` : "All cleared"} to="/app/memberships" />
                        <ReportStatCard icon={Users} iconTone="labor" label="Labor cost" value={inr(data.labor_cost_total)} sub={data.labor_cost_pct != null ? `${data.labor_cost_pct}% of revenue` : "Staff payroll"} to="/app/staff" />
                        <ReportStatCard icon={Users} iconTone="customers" label="New customers" value={data.new_customers} sub="Joined in period" to="/app/customers" />
                        <ReportStatCard icon={Calendar} iconTone="discount" label="Appointments" value={data.appointments_total} sub="In period" to="/app/calendar" />
                    </div>

                    <RevenueTrendChart trend={data.by_day} days={reportDays} />

                    <div className="grid gap-5 lg:grid-cols-2">
                        <InvoiceVolumeChart trend={data.by_day_invoices} days={reportDays} />
                        <PaymentModeChart payments={data.by_payment} />
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2">
                        <StaffRevenueChart staff={data.by_staff} />
                        <TopServicesChart services={data.by_service || []} />
                    </div>

                    <StaffPayrollTable
                        rows={data.staff_payroll || []}
                        laborTotal={data.labor_cost_total || 0}
                        laborPct={data.labor_cost_pct}
                        revenueTotal={data.total}
                    />

                    <div className="grid gap-5 lg:grid-cols-2">
                        <MoneyBreakdown data={data} />
                        <RevenueTypeChart types={data.by_revenue_type} />
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2">
                        <GstSummary data={data} />
                        <AppointmentBreakdown statuses={data.appointment_by_status || []} total={data.appointments_total} />
                    </div>

                    <MembershipOutstandingTable
                        rows={data.membership_outstanding || []}
                        total={data.membership_outstanding_total || 0}
                    />

                    <InvoiceLedger ledger={data.ledger || []} />

                    <div className="flex flex-wrap items-center justify-center gap-2 rounded-xl border border-dashed border-[#E8E4DE] bg-[#FAF9F6]/60 px-4 py-3 text-center text-xs font-medium text-[#8A8178]">
                        <FileText className="size-3.5 text-[#D4AF37]" />
                        Export full report as CSV or print a summary for your accountant.
                        {(data.membership_outstanding_total > 0) && (
                            <>
                                <span className="text-[#E8E4DE]">·</span>
                                <Wallet className="size-3.5 text-[#C84B31]" />
                                {inr(data.membership_outstanding_total)} membership dues outstanding
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
